import React from 'react'
import styles from './Gt2FeatureRows.module.css'
import OptimizedImage from '../../../OptimizedImage'
import {
  ProfileIcon,
  WrenchIcon,
  TorqueIcon,
  ShieldIcon,
  ColoursIcon,
  ModularIcon,
  AdjustIcon,
  SeatIcon,
  RailsIcon,
  EuIcon,
} from '../../ComparisonTable/icons'

// Alternating image/text feature rows below the shop proof band (the Sim-Lab
// "scroll down = image left / text right, then flipped" layout). Tells the
// best-quality-cockpit story. Images are the GT-2 studio renders (managed assets;
// descriptors built with absolute media URLs so they render without the #67
// server resolver and without tripping no-raw-media-src, which only flags JSX
// src/placeholderSrc string literals). First draft, copy pending sign-off.
// Studio shots, cropped to the 3:2 box these rows render in (1400x933) so they
// are sharp rather than a huge image scaled down: 13MB -> 265KB for the four.
// The same images also seed the black gallery (see getConfigurationImages).
// studio-4 was a PORTRAIT frame pillarboxed onto the 1400x933 canvas, so the
// "ready to race" row rendered as a small vertical rig floating in grey bars.
// Replaced with studio-5, a landscape frame of the same shoot processed the
// same way (1400x933 WebP, 68KB). studio-4 is left in place, still used by the
// gallery, where the crop is not a problem.
const MEDIA = 'https://storage.googleapis.com/vrs-media/assets/images/gt2-cockpit'
const img = (n) => ({ src: `${MEDIA}/gt2-studio-${n}.webp`, width: 1400, height: 933, alt: 'VRS DirectForce GT-2 Cockpit' })

// Outcome-led headlines with the engineering proof kept directly underneath:
// the buyer reads the benefit first (no flex, lasts, fits everything, race
// today), then the spec that earns it. Do not flip these back to spec-first
// titles; the numbers on their own do not sell the rig.
//
// Each row: one big red figure (`stat`), a two-line body and 2-3 short points.
// The long-form engineering copy lives in the specs tabs further down.
const ROWS = [
  {
    kicker: 'Built from better material',
    title: 'Zero flex. Total confidence.',
    stat: { value: '160 x 40', unit: 'mm', caption: 'thick-wall aluminium profile' },
    body: 'Extruded from our own custom mould, with 10 mm laser-cut steel plate. The rig stays dead rigid, so what you feel is the car, not the cockpit.',
    points: [
      { Icon: ProfileIcon, text: 'Thick-wall profile, not off-the-shelf thin-wall' },
      { Icon: WrenchIcon, text: '10 mm laser-cut carbon steel plate' },
      { Icon: TorqueIcon, text: 'Built for direct drive up to 45 Nm' },
    ],
    image: img(1),
  },
  {
    kicker: 'Premium finish',
    title: 'Still looks new after years of racing',
    stat: { value: 'EU', unit: '', caption: 'made structural powder coat' },
    body: 'Every profile wears an industrial-grade matte powder coat for a deep, even finish that keeps its looks lap after lap.',
    points: [
      { Icon: ShieldIcon, text: 'Resists scratches and wear' },
      { Icon: ColoursIcon, text: 'Matte finish in 7 colours' },
    ],
    image: img(2),
  },
  {
    kicker: 'Fully configurable',
    title: 'Mount anything, anywhere, any time',
    stat: { value: 'Every', unit: 'face', caption: 'of the profile is slotted' },
    body: 'Wheelbases, pedals, shifters and accessories bolt on anywhere on the frame. Change your gear and the cockpit adapts instead of being replaced.',
    points: [
      { Icon: ModularIcon, text: 'Accessory mounting left and right' },
      { Icon: AdjustIcon, text: 'Straight uprights, easy to dial in' },
      { Icon: SeatIcon, text: 'Rally and GT seating positions' },
    ],
    image: img(3),
  },
  {
    kicker: 'Ready to race',
    title: 'Racing the day it arrives',
    stat: { value: '95', unit: '%', caption: 'pre-assembled at our factory' },
    body: 'No flat-pack build. The GT-2 arrives almost complete in a single box: bolt on your gear and drive.',
    points: [
      { Icon: RailsIcon, text: 'Seat sliders and brackets included' },
      { Icon: EuIcon, text: 'Designed and made in the EU' },
    ],
    image: img(5),
  },
]

function Gt2FeatureRows() {
  return (
    <section className={styles.wrap} aria-label="Why the GT-2 is the best-built cockpit">
      <header className={styles.head}>
        <span className={styles.eyebrow}>No compromises</span>
        <h2 className={styles.heading}>The best-built sim racing cockpit you can buy</h2>
      </header>
      {ROWS.map((r, i) => (
        <div className={[styles.row, i % 2 === 1 && styles.reversed].filter(Boolean).join(' ')} key={r.title}>
          <div className={styles.imageCol}>
            <div className={styles.imageBox}>
              <OptimizedImage image={r.image} alt={r.image.alt} layout="fill" objectFit="cover" sizes="(max-width: 767px) 100vw, 60vw" reserveSpace={false} />
            </div>
          </div>
          <div className={styles.textCol}>
            <span className={styles.rowNo} aria-hidden="true">{String(i + 1).padStart(2, '0')}</span>
            <span className={styles.kicker}>{r.kicker}</span>
            <h3 className={styles.title}>{r.title}</h3>
            <p className={styles.stat}>
              <span className={styles.statValue}>{r.stat.value}{r.stat.unit && <span className={styles.statUnit}> {r.stat.unit}</span>}</span>
              <span className={styles.statCaption}>{r.stat.caption}</span>
            </p>
            <p className={styles.body}>{r.body}</p>
            <ul className={styles.points}>
              {r.points.map(({ Icon, text }) => (
                <li className={styles.point} key={text}>
                  <span className={styles.pointIcon}><Icon size={18}/></span>
                  {text}
                </li>
              ))}
            </ul>
          </div>
        </div>
      ))}
    </section>
  )
}

export default Gt2FeatureRows
